import { useEffect, useState } from 'react';
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';
import { toast } from 'sonner';
import { GripVertical, ArrowUp, ArrowDown, Loader2, Save, ListOrdered } from 'lucide-react';

const SortableRow = ({ course, index, total, move }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: course.id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, borderColor: isDragging ? '#163329' : '#E3E1DB', zIndex: isDragging ? 10 : undefined }}
      className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border bg-white ${isDragging ? 'shadow-lg' : ''}`}
      data-testid={`order-row-${course.id}`}
    >
      <button type="button" className="cursor-grab touch-none p-1 rounded hover:bg-[#F0EFEA]" {...attributes} {...listeners} aria-label="Drag to reorder" data-testid={`order-drag-${course.id}`}>
        <GripVertical className="h-5 w-5" style={{ color: '#5C635A' }} />
      </button>
      <span className="w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 text-white" style={{ backgroundColor: '#163329' }} data-testid={`order-number-${course.id}`}>
        {index + 1}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate" style={{ color: '#163329' }}>{course.title}</p>
        <p className="text-xs truncate" style={{ color: '#5C635A' }}>{course.teacher_name}</p>
      </div>
      <div className="flex gap-1 flex-shrink-0">
        <Button type="button" size="icon" variant="ghost" className="h-8 w-8" disabled={index === 0} onClick={() => move(index, index - 1)} aria-label="Move up" data-testid={`order-up-${course.id}`}>
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button type="button" size="icon" variant="ghost" className="h-8 w-8" disabled={index === total - 1} onClick={() => move(index, index + 1)} aria-label="Move down" data-testid={`order-down-${course.id}`}>
          <ArrowDown className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export const CourseOrderCard = ({ courses, onSaved }) => {
  const [items, setItems] = useState(courses);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => { setItems(courses); setDirty(false); }, [courses]);

  const move = (from, to) => {
    setItems((prev) => arrayMove(prev, from, to));
    setDirty(true);
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    move(items.findIndex((c) => c.id === active.id), items.findIndex((c) => c.id === over.id));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/courses/order', { course_ids: items.map((c) => c.id) });
      toast.success('Explore Courses order saved');
      setDirty(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-6" style={{ borderColor: '#E3E1DB' }} data-testid="course-order-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
          <ListOrdered className="h-5 w-5" /> Explore Courses Order
        </CardTitle>
        <p className="text-sm" style={{ color: '#5C635A' }}>Drag courses (or use the arrows) into the order they should appear on the Explore Courses page, then save.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((c) => c.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {items.map((course, index) => (
                <SortableRow key={course.id} course={course} index={index} total={items.length} move={move} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        <Button onClick={save} disabled={!dirty || saving} style={{ backgroundColor: '#163329' }} data-testid="save-course-order-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Order
        </Button>
      </CardContent>
    </Card>
  );
};
